import { useEffect, useMemo, useState } from "react";
import style from "./index.module.scss";
import UnionIcon from "../assets/union.svg";
import CloseIcon from "../assets/close.svg";
import AudioIcon from "../assets/audio.svg";
import FileIcon from "../assets/file.svg";
import ImageIcon from "../assets/image.svg";
import AudioUploadIcon from "../assets/audio-upload.svg";
import FileUploadIcon from "../assets/file-upload.svg";
import DeleteIcon from "../assets/delete.svg";
import { Button, Popup, SafeArea, Toast, SpinLoading } from "antd-mobile";
import classnames from "classnames";
import ErrorIcon from "../assets/error.svg";

import { useHistory, useParams } from "react-router-dom";
import {
  fileSubmit,
  validateVerifyCode,
  verifySmsUrl,
  queryCommonParam,
} from "../../../network/workUploadApi";
import { upload } from "./utils";
import { useRouteQuery } from "../../../utils";

const FileUpload = () => {
  const phone = sessionStorage.getItem("phone");
  const code = sessionStorage.getItem("code");
  const { token } = useParams<{ token: string }>();
  const templateCode = useRouteQuery("templateCode");
  const [tipMsg, setTipMsg] = useState<string>(
    "单个文件最大不超过300M，最多上传5份文件",
  );
  const history = useHistory();
  const [fileList, setFileList] = useState<
    { file: File; id: number; response: any }[]
  >([]);
  const [uploadVisible, setUploadVisible] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [loadingMap, setLoadingMap] = useState<{ [key: number]: 0 | 1 | 2 }>(
    {},
  );

  // 上传处理
  const handleUpload = async (e) => {
    const { files } = e.target;
    const file = files?.[0] || {};

    if (file.size > 1024 * 1024 * 300) {
      return Toast.show("上传的文件大小不得超过300M");
    }

    setUploadVisible(false);

    const id = Date.now();
    const fileItem = {
      file,
      id,
      response: {},
    };
    setFileList((prev) => [...prev, fileItem]);
    try {
      setLoadingMap((prev) => ({ ...prev, [id]: 0 }));
      await upload({
        file,
        onSuccess: async (res) => {
          setFileList((prev) => {
            const arr = [...prev];
            arr.forEach((item) => {
              if (item.id === id) {
                item.response = res;
              }
            });
            return arr;
          });
        },
        token,
        mobile: phone,
      });
      setLoadingMap((prev) => ({ ...prev, [id]: 1 }));
    } catch {
      setLoadingMap((prev) => ({ ...prev, [id]: 2 }));
    } finally {
      e.target.value = "";
    }
  };

  useEffect(() => {}, [fileList]);

  // 删除文件
  const handleDelete = (index) => {
    const arr = [...fileList];
    const deleteItem = arr.splice(index, 1);
    setLoadingMap((prev) => {
      const newMap = { ...prev };
      delete newMap[deleteItem?.[0]?.id];
      return newMap;
    });
    setFileList(arr);
  };

  // 提交
  const handleSubmit = async () => {
    let attachArr: any[] = [];
    if (Array.isArray(fileList) && fileList.length > 0) {
      fileList.forEach((file: any) => {
        if (file?.response?.data?.fileId) {
          attachArr.push(file?.response?.data?.fileId);
        }
      });
    }

    const params = {
      fileIds: attachArr,
      mobile: phone,
      token,
      verifyCode: code,
    };
    try {
      setSubmitLoading(true);
      await fileSubmit(params);
      setSubmitLoading(false);
      setFileList([]);
      history.replace({
        pathname: `/work-upload/result/${token}`,
        state: {
          status: "success",
        },
      });
    } catch (e: any) {
      setSubmitLoading(false);
      const msg = e.response?.data?.msg || "网络异常";
      Toast.show(msg);
    }
  };

  const submitDisabled = useMemo(() => {
    const isUploading = Object.values(loadingMap).some((item) => item === 0);
    return !fileList.length || isUploading;
  }, [fileList, loadingMap]);

  useEffect(() => {
    document.title = "文件上传";
    //  校验验证码
    const handleVerifyCode = async () => {
      const path = {
        pathname: `/work-upload/verify/${token}`,
        search: templateCode ? `?templateCode=${templateCode}` : "",
      };
      if (!phone || !code) {
        history.replace({ ...path });
        return;
      }
      try {
        const params = {
          token,
          mobile: phone,
          verifyCode: code,
        };
        await validateVerifyCode(params);
      } catch (e: any) {
        console.error("handleVerifyCode", e);
        history.replace({ ...path });
      }
    };
    const handleVerify = async () => {
      // 校验链接
      const isValid = /^[\u0020-\u007F]+$/.test(token);
      if (!isValid) {
        history.replace(`/work-upload/error/${token}`);
        return;
      }
      try {
        await verifySmsUrl({ token });
        await handleVerifyCode();
      } catch (e: any) {
        console.error("handleVerify", e);
        history.replace(`/work-upload/error/${token}`);
      }
    };
    handleVerify();

    const fetchTips = async () => {
      try {
        const res: any = await queryCommonParam({
          code: "customerUploadFileSmsTemplateCode",
          key: templateCode,
          token,
        });
        const text = res.data?.data?.value;
        if (text) {
          const htmlString = text.replace(/\n/g, "<br />");
          setTipMsg(htmlString);
        }
      } catch (err) {}
    };
    if (templateCode) fetchTips();
  }, [code, history, phone, templateCode, token]);

  return (
    <div className={style.fileUploadPage}>
      <div className={style.tip} dangerouslySetInnerHTML={{ __html: tipMsg }} />
      <div className={style.fileUploader}>
        {fileList.map((item, index) => {
          const { file, id } = item || {};
          let src = "";
          if (file.type?.indexOf("image") !== -1) {
            src = URL.createObjectURL(file);
          } else if (file.type?.indexOf("audio") !== -1) {
            src = AudioUploadIcon;
          } else {
            src = FileUploadIcon;
          }

          const status = loadingMap[id];
          return (
            <div className={style.uploadBox} key={index}>
              {(status === 0 || status === 2) && (
                <div className={style.loadingWrap}>
                  {status === 2 ? (
                    <img src={ErrorIcon} alt="errorIcon" />
                  ) : (
                    <SpinLoading />
                  )}
                </div>
              )}
              <img
                alt=""
                src={src}
                className={classnames({
                  [style.image]: file.type?.indexOf("image") !== -1,
                })}
              />
              <img
                alt="deleteIcon"
                src={DeleteIcon}
                className={style.deleteIcon}
                onClick={() => handleDelete(index)}
              />
            </div>
          );
        })}
        {fileList.length < 5 && (
          <div
            className={style.uploadBtn}
            onClick={() => setUploadVisible(true)}
          >
            <img src={UnionIcon} alt="uploadIcon" />
            <div className={style.uploadText}>上传文件</div>
          </div>
        )}
      </div>
      <div className={style.footer}>
        <Button
          block
          size="large"
          disabled={submitDisabled}
          loading={submitLoading}
          onClick={handleSubmit}
        >
          提交
        </Button>
        <SafeArea position="bottom" />
      </div>
      <Popup
        visible={uploadVisible}
        onMaskClick={() => {
          setUploadVisible(false);
        }}
        bodyClassName={style.uploadPopup}
      >
        <div className={style.header}>
          <div
            className={style.closeBtn}
            onClick={() => setUploadVisible(false)}
          >
            <img src={CloseIcon} alt="closeIcon" />
          </div>
        </div>
        <div className={style.content}>
          <div className={style.item}>
            <img src={ImageIcon} alt="imageIcon" />
            <div className={style.text}>图片</div>
            <input
              type="file"
              accept="image/*"
              className={style.fileInput}
              onChange={handleUpload}
            />
          </div>
          <div className={style.item}>
            <img src={AudioIcon} alt="audioIcon" />
            <div className={style.text}>音频</div>
            <input
              type="file"
              accept="audio/*"
              className={style.fileInput}
              onChange={handleUpload}
            />
          </div>
          <div className={style.item}>
            <img src={FileIcon} alt="fileIcon" />
            <div className={style.text}>文件</div>
            <input
              type="file"
              accept="*"
              className={style.fileInput}
              onChange={handleUpload}
            />
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default FileUpload;
